/**
 * Ventana «Próximos a vencer» del inicio.
 *
 * Es lo que pidio el dueno, textual: «que agreguen una ventana al inicio que
 * diga proximos y aparezcan los que esten proximos a vencer para llamarlos». El
 * objetivo es LLAMAR, asi que cada renglon trae el telefono y el email a la
 * vista —un `tel:` y un `mailto:` de un toque— y no obliga a navegar tres
 * pantallas para encontrar con quien comunicarse.
 *
 * Los datos son REALES y salen de la API generica, nada hardcodeado:
 *
 *  1. `sales.subscription` filtrado por `state=active` y `end_date <= hoy + N`,
 *     ordenado por vencimiento. El filtro por fecha es exacto siempre —no
 *     depende del semaforo `vigencia`, que se recalcula al guardar— asi que la
 *     agenda nunca miente sobre a quien hay que llamar hoy.
 *  2. `base.partner` de esos clientes, para traer telefono y email, que la
 *     lista de suscripciones no incluye (un many2one solo trae id + nombre).
 *
 * El boton «Ya lo contacte» dispara la accion de servidor `registrar_contacto`,
 * que deja el llamado en el chatter del abono: el seguimiento queda en el
 * nucleo y no en la cabeza del vendedor.
 *
 * Este panel es duenno SOLO de la seccion «Proximos». El resto del inicio lo
 * maneja otro agente; por eso vive en su propio componente y `HomePage` lo
 * monta, en vez de tener todo mezclado en un archivo.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { daysUntil, formatCurrency, formatDate } from '@/components/fields/format';
import { IconAlert, IconCheck, IconChevronRight, IconSpinner } from '@/components/shell/icons';
import { recordPath, modelPath } from '@/components/shell/menu-utils';
import { Badge, Button, Card } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import { api, ApiError } from '@/lib/api';
import type { Domain, Many2OneValue } from '@/lib/types';
import { useSessionStore } from '@/store/session';

const MODELO = 'sales.subscription';

/** Horizonte de la agenda: hasta cuantos dias adelante se considera «proximo». */
const HORIZONTES = [
  { dias: 7, label: 'Esta semana' },
  { dias: 30, label: '30 días' },
  { dias: 60, label: '60 días' },
] as const;

/** Etiqueta de la frecuencia de renovacion, para no mostrar el codigo crudo. */
const FRECUENCIA: Record<string, string> = {
  monthly: 'Mensual',
  quarterly: 'Trimestral',
  biannual: 'Semestral',
  annual: 'Anual',
};

/** Una fila de la agenda, ya cruzada con los datos de contacto del cliente. */
interface FilaVencimiento {
  id: number;
  clienteId: number | null;
  cliente: string;
  que: string;
  vence: string;
  dias: number;
  importe: string;
  frecuencia: string;
  telefono: string;
  email: string;
  contactadoHoy: boolean;
}

function fechaHoyIso(): string {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const y = hoy.getFullYear();
  const m = String(hoy.getMonth() + 1).padStart(2, '0');
  const d = String(hoy.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** «Vencio hace 3 dias» / «Vence hoy» / «Vence en 5 dias». */
function urgencia(dias: number): { texto: string; tone: 'danger' | 'warning' } {
  if (dias < 0) {
    const n = Math.abs(dias);
    return { texto: n === 1 ? 'Venció ayer' : `Venció hace ${n} días`, tone: 'danger' };
  }
  if (dias === 0) return { texto: 'Vence hoy', tone: 'danger' };
  if (dias === 1) return { texto: 'Vence mañana', tone: 'warning' };
  return { texto: `Vence en ${dias} días`, tone: 'warning' };
}

function textoDe(valor: unknown): string {
  return typeof valor === 'string' ? valor : '';
}

/**
 * Trae los abonos por vencer y les cruza el telefono y el email del cliente.
 *
 * Son dos consultas a la API generica y no una: la lista de suscripciones trae
 * el cliente como many2one (id + nombre), sin telefono ni email, asi que se
 * juntan los ids y se pide `base.partner` de esos contactos en un solo viaje.
 */
async function cargarAgenda(horizonteDias: number, signal: AbortSignal): Promise<FilaVencimiento[]> {
  const hoyIso = fechaHoyIso();
  const hoyMas = new Date();
  hoyMas.setHours(0, 0, 0, 0);
  hoyMas.setDate(hoyMas.getDate() + horizonteDias);
  const hastaIso = [
    hoyMas.getFullYear(),
    String(hoyMas.getMonth() + 1).padStart(2, '0'),
    String(hoyMas.getDate()).padStart(2, '0'),
  ].join('-');

  const dominio: Domain = [
    ['state', '=', 'active'],
    ['end_date', '<=', hastaIso],
  ];
  const abonos = await api.searchRead(
    MODELO,
    {
      domain: dominio,
      order: 'end_date',
      limit: 50,
      fields: ['partner', 'name', 'end_date', 'amount', 'renewal_period', 'last_contact_date'],
    },
    signal,
  );

  const ids = Array.from(
    new Set(
      abonos.results
        .map((fila) => (fila.partner as Many2OneValue | null)?.id)
        .filter((id): id is number => typeof id === 'number'),
    ),
  );

  const contactos = new Map<number, { telefono: string; email: string }>();
  if (ids.length > 0) {
    const partners = await api.searchRead(
      'base.partner',
      { domain: [['id', 'in', ids]], fields: ['phone', 'email'], limit: ids.length },
      signal,
    );
    for (const p of partners.results) {
      contactos.set(p.id, { telefono: textoDe(p.phone), email: textoDe(p.email) });
    }
  }

  return abonos.results.map((fila): FilaVencimiento => {
    const cliente = fila.partner as Many2OneValue | null;
    const contacto = cliente !== null ? contactos.get(cliente.id) : undefined;
    const vence = textoDe(fila.end_date);
    return {
      id: fila.id,
      clienteId: cliente?.id ?? null,
      cliente: cliente?.display_name ?? 'Sin cliente',
      que: textoDe(fila.name),
      vence,
      dias: daysUntil(vence) ?? 0,
      importe: textoDe(fila.amount),
      frecuencia: FRECUENCIA[textoDe(fila.renewal_period)] ?? '',
      telefono: contacto?.telefono ?? '',
      email: contacto?.email ?? '',
      contactadoHoy: textoDe(fila.last_contact_date) === hoyIso,
    };
  });
}

export function ProximosPanel() {
  const companyId = useSessionStore((estado) => estado.activeCompanyId);
  const empresas = useSessionStore((estado) => estado.companies);
  const autenticado = useSessionStore((estado) => estado.status === 'authenticated');
  const [horizonte, setHorizonte] = useState<number>(30);
  const [contactando, setContactando] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const claveBase = ['proximos-vencer', companyId] as const;
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: [...claveBase, horizonte],
    queryFn: ({ signal }) => cargarAgenda(horizonte, signal),
    enabled: autenticado,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  async function marcarContactado(fila: FilaVencimiento): Promise<void> {
    setContactando(fila.id);
    try {
      await api.callAction(MODELO, fila.id, 'registrar_contacto', {
        nota: 'Contactado desde la agenda de vencimientos del inicio.',
      });
      notify.success('Contacto registrado', `Quedó anotado en el historial de ${fila.cliente}.`);
      await queryClient.invalidateQueries({ queryKey: claveBase });
    } catch (e) {
      const mensaje = e instanceof ApiError ? e.message : 'No se pudo registrar el contacto.';
      notify.error('No se pudo registrar', mensaje);
    } finally {
      setContactando(null);
    }
  }

  // Si el usuario no puede leer los abonos —un operador o un propietario, que no
  // tienen ese permiso— el panel se oculta en silencio en vez de escupir «No
  // tenés permiso de read sobre sales.subscription». Es lo primero que se ve al
  // entrar; un error tecnico ahi asusta y filtra el nombre del modelo. Mismo
  // criterio si el modelo no esta (404): sin ventas instaladas no hay agenda que
  // mostrar. El resto de los errores (red, 500) si se muestran: son transitorios
  // y el boton «Reintentar» tiene sentido.
  if (isError && error instanceof ApiError && (error.isPermissionError || error.status === 404)) {
    return null;
  }

  const filas = data ?? [];
  /**
   * Nombre de la empresa activa, para no afirmar de mas cuando no hay nada.
   *
   * La agenda consulta SIEMPRE con la cabecera `X-Company-Id`, asi que lo que
   * mira es una empresa y no el sistema entero. El cartel de vacio decia «No hay
   * planes ni contratos que venzan…», en absoluto, y quien administra tres
   * consorcios lo leia como «no hay nada en ningun lado»: con la empresa
   * equivocada seleccionada, eso es exactamente lo contrario de la verdad y deja
   * de llamar a un cliente. Con el nombre a la vista, el vacio se lee como lo
   * que es —un vacio de ESTA empresa— y ademas dice donde mirar si no cierra.
   */
  const empresaActiva = empresas.find((empresa) => empresa.id === companyId)?.name ?? null;

  return (
    <section aria-labelledby="proximos-titulo" className="mt-2">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2
          id="proximos-titulo"
          className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-fg-faint"
        >
          <IconAlert className="h-4 w-4 text-warning" />
          Próximos a vencer
        </h2>
        <div className="flex items-center gap-1 rounded-lg border border-line bg-surface p-0.5" role="group" aria-label="Horizonte de la agenda">
          {HORIZONTES.map((h) => (
            <button
              key={h.dias}
              type="button"
              onClick={() => setHorizonte(h.dias)}
              aria-pressed={horizonte === h.dias}
              className={
                'rounded-md px-2.5 py-1 text-xs font-medium transition-colors ' +
                (horizonte === h.dias
                  ? 'bg-accent-soft text-accent'
                  : 'text-fg-muted hover:bg-surface-raised')
              }
            >
              {h.label}
            </button>
          ))}
        </div>
      </div>

      <Card className="overflow-hidden">
        {isPending ? (
          <div className="flex items-center justify-center gap-3 py-12 text-fg-muted">
            <IconSpinner className="h-5 w-5" />
            <span className="text-sm">Buscando lo que está por vencer…</span>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center">
            <IconAlert className="h-6 w-6 shrink-0 text-danger" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold">No se pudo cargar la agenda</p>
              <p className="mt-1 text-sm text-fg-muted">
                {error instanceof ApiError ? error.message : 'Reintentá en un momento.'}
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              Reintentar
            </Button>
          </div>
        ) : filas.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-success-soft text-success">
              <IconCheck className="h-6 w-6" />
            </span>
            <p className="mt-3 text-base font-semibold">
              Nada por vencer en {empresaActiva ?? 'esta empresa'}
            </p>
            <p className="mt-1 max-w-sm text-sm text-fg-muted">
              {empresaActiva === null
                ? 'La empresa con la que estás trabajando no tiene planes ni contratos que venzan '
                : `${empresaActiva} no tiene planes ni contratos que venzan `}
              en los próximos {horizonte} días. Cuando se acerque un vencimiento, va a aparecer acá
              con el teléfono a mano para llamar.
            </p>
            <p className="mt-2 max-w-sm text-xs text-fg-faint">
              La agenda mira sólo la empresa activa. Si esperabas ver algo, cambiala arriba a la
              derecha.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {filas.map((fila) => {
              const marca = urgencia(fila.dias);
              const ocupado = contactando === fila.id;
              return (
                <li
                  key={fila.id}
                  className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={marca.tone}>{marca.texto}</Badge>
                      <span className="text-xs text-fg-faint">
                        {formatDate(fila.vence)}
                        {fila.frecuencia !== '' ? ` · ${fila.frecuencia}` : ''}
                      </span>
                      {fila.contactadoHoy && (
                        <Badge tone="success">
                          <IconCheck className="h-3 w-3" /> Contactado hoy
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
                      <Link
                        to={recordPath(MODELO, fila.id)}
                        className="truncate font-medium hover:text-accent"
                      >
                        {fila.cliente}
                      </Link>
                      <span className="truncate text-sm text-fg-muted">— {fila.que}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                      <span className="font-semibold tabular-nums">
                        {formatCurrency(fila.importe || '0', { currency: 'ARS', minimumFractionDigits: 2 })}
                      </span>
                      {fila.telefono !== '' ? (
                        <a
                          href={`tel:${fila.telefono.replace(/\s+/g, '')}`}
                          className="inline-flex items-center gap-1 text-accent hover:underline"
                        >
                          <IconPhone className="h-3.5 w-3.5" />
                          {fila.telefono}
                        </a>
                      ) : (
                        <span className="text-fg-faint">Sin teléfono</span>
                      )}
                      {fila.email !== '' && (
                        <a
                          href={`mailto:${fila.email}`}
                          className="inline-flex items-center gap-1 text-fg-muted hover:text-accent hover:underline"
                        >
                          <IconMail className="h-3.5 w-3.5" />
                          {fila.email}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      variant={fila.contactadoHoy ? 'secondary' : 'primary'}
                      size="sm"
                      loading={ocupado}
                      disabled={ocupado}
                      onClick={() => void marcarContactado(fila)}
                    >
                      {!ocupado && <IconCheck className="mr-1 h-4 w-4" />}
                      {fila.contactadoHoy ? 'Contactar otra vez' : 'Ya lo contacté'}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {filas.length > 0 && (
          <div className="border-t border-line bg-surface-raised/40 px-4 py-2.5 text-right">
            <Link
              to={modelPath(MODELO, 'sales.menu_subscription_due')}
              className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
            >
              Ver la agenda completa
              <IconChevronRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Iconos locales
// ---------------------------------------------------------------------------
// El set compartido no trae telefono ni sobre, y son los dos gestos centrales de
// este panel (llamar y escribir). Se definen aca, inline, para no tener que
// tocar el modulo de iconos —que es territorio comun— por un panel.
function IconPhone(props: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={props.className}
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function IconMail(props: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={props.className}
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 6L2 7" />
    </svg>
  );
}
